/**
 * canonical.js — MOAT 1.0 · ONE source of truth for platform-wide facts.
 * Re-exports existing canonical modules (never a second copy of the data) and
 * adds the few values that were previously hard-coded in many places.
 */
import { TIERS, OVERALL_RANGE } from "@/lib/ageConfig";
import { SUBJECTS } from "@/lib/curriculum";
import { COUNTRY_COUNT, COUNTRIES_LABEL } from "@/lib/siteFacts";

export const AGE_BANDS = TIERS.map((t) => { const [lo, hi] = t.range.split("–").map(Number); return { ...t, min: lo, max: hi }; });
export const AGE_RANGE = OVERALL_RANGE;
export const JOURNEY_WEEKS = 36;
export const YEARS = Array.from({ length: 10 }, (_, i) => i + 1);
export { SUBJECTS, COUNTRY_COUNT, COUNTRIES_LABEL };

export const bandForAge = (age) => AGE_BANDS.find((b) => age >= b.min && age <= b.max)?.id || (age < 4 ? "seedlings" : "pioneers");
export const yearForAge = (age) => Math.max(1, Math.min(10, Math.round(Number(age)) - 4));
export const ageForYear = (year) => Math.max(4, Math.min(15, Number(year) + 4));
export const bandForYear = (year) => bandForAge(ageForYear(year));
/** US grade ↔ UK year (UK Year N ≈ US Grade N−1; Year 1 ≈ Kindergarten). */
export const gradeForYear = (year) => (year <= 1 ? "K" : String(year - 1));

export const CURRICULA = [
  { id: "uk", label: "UK National Curriculum" }, { id: "ib", label: "IB PYP / MYP" }, { id: "cambridge", label: "Cambridge Primary / Lower Secondary" },
  { id: "uae", label: "UAE MoE" }, { id: "us", label: "US Common Core" }, { id: "cbse", label: "CBSE" },
];
export const ALIGNMENT_WORDING = "aligned to"; // never "accredited"/"approved"
// Family ids above (uk · ib · cambridge · uae · us · cbse) vs programme ids in lib/syllabus (uk-ks2 · ib-pyp · cam-prim · uae-moe · common-core).
const FAMILY_PREFIX = { uk: ["uk-", "eyfs"], ib: ["ib-"], cambridge: ["cam-"], uae: ["uae-"], us: ["common-core"], cbse: ["cbse"] };
export const matchesCurriculum = (programmeId, wanted) => !wanted || programmeId === wanted || (FAMILY_PREFIX[wanted] || []).some((p) => String(programmeId || "").startsWith(p));

/** Phase 27 · one canonical feature status vocabulary. */
export const STATUS = { LIVE: "LIVE", BETA: "BETA", COMING_SOON: "COMING SOON", ADULT_ONLY: "ADULT ONLY", TEACHER_ONLY: "TEACHER ONLY", SCHOOL_ONLY: "SCHOOL ONLY" };
export const FEATURE_STATUS = {
  "live-quest": STATUS.LIVE, "live-teaching": STATUS.COMING_SOON, "ai-quest": STATUS.LIVE, "ai-coach": STATUS.BETA, "focus-day": STATUS.LIVE,
  "parent-goals": STATUS.ADULT_ONLY, "weekly-report": STATUS.ADULT_ONLY, "teach-my-child": STATUS.ADULT_ONLY, "teacher-studio": STATUS.TEACHER_ONLY,
  "school-intelligence": STATUS.SCHOOL_ONLY, "code-quest": STATUS.LIVE, "english-quest": STATUS.LIVE, "ttqs": STATUS.LIVE, "treasure-quest": STATUS.LIVE, "future-lab": STATUS.COMING_SOON,
};
export const featureStatus = (id) => FEATURE_STATUS[id] || STATUS.LIVE;

export const REWARDS = { starsKey: "we_progress_v1", masteryKey: "we_mastery_v1", ledgerKey: "we_skill_agg_v1", ttqsKey: "mwq_ttqs_v1", focusKey: "mwq_focus_v1", goalsKey: "mwq_parent_goals_v1", memoryKey: "mwq_learning_memory_v1" };

/** Contradiction scan — returns [] when canonical values agree. */
export function canonicalConsistency() {
  const issues = [];
  const covered = AGE_BANDS.every((b, i) => i === 0 || b.min === AGE_BANDS[i - 1].max + 1);
  if (!covered) issues.push("Age bands are not contiguous");
  if (`${AGE_BANDS[0].min}–${AGE_BANDS[AGE_BANDS.length - 1].max}` !== AGE_RANGE) issues.push(`AGE_RANGE ${AGE_RANGE} ≠ bands`);
  if (COUNTRY_COUNT < 190) issues.push(`Country count ${COUNTRY_COUNT} looks wrong`);
  if (SUBJECTS.length !== new Set(SUBJECTS.map((s) => s.id)).size) issues.push("Duplicate subject ids");
  for (let y = 1; y <= 10; y += 1) if (yearForAge(ageForYear(y)) !== y) issues.push(`Year ${y} age mapping not reversible`);
  return issues;
}
